import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Map, RefreshCw, Calculator, FileDown, Search,
  Zap, ArrowRight, ChevronRight, Shield,
  Layers, Crosshair, FolderOpen, Globe,
  Mail, Phone, MapPin, Github, Linkedin,
  CheckCircle, Star, Menu, X,
} from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore';
import LogoWordmark from '../components/LogoWordmark';
import './HomePage.css';

const FEATURES = [
  {
    icon: Map, color: '#667eea', badge: 'Leaflet · CartoDB',
    title: 'Carte interactive',
    description: 'Carte sombre CartoDB avec fond satellite/OSM. Mesure de distance et surface (WGS84 ou Laborde), recherche de points géodésiques par rayon et changement de système de coordonnées en temps réel.',
  },
  {
    icon: RefreshCw, color: '#10b981', badge: 'Batch & Manuel',
    title: 'Transformation géodésique',
    description: 'Convertissez WGS84, UTM (zones 38S/39S) et Laborde Madagascar. Saisie manuelle ou import CSV/TXT par lot avec téléchargement automatique du fichier converti.',
  },
  {
    icon: Calculator, color: '#f59e0b', badge: '7 Paramètres',
    title: 'Calcul Helmert',
    description: 'Calculez les 7 paramètres de Helmert (Tx, Ty, Tz, k, Rx, Ry, Rz) en sélectionnant des points géodésiques sur la carte. Résidus (ΔX, ΔY) affichés par point d\'adaptation.',
  },
  {
    icon: FileDown, color: '#ef4444', badge: 'DXF · PDF · Three.js',
    title: 'Viewer DXF / CAO',
    description: 'Importez vos fichiers DXF et visualisez-les en 2D. Outils de dessin : Ligne, Polyligne, Cercle, Rectangle, Hachure, Texte. Suppression, gestion des calques et export DXF + PDF à l\'échelle exacte.',
  },
  {
    icon: FolderOpen, color: '#8b5cf6', badge: 'Cloud',
    title: 'Stockage de fichiers',
    description: 'Gérez vos fichiers géodésiques dans votre espace personnel sécurisé. Upload par glisser-déposer, suivi du quota, support DXF, GSI, CSV, SHP et PDF.',
  },
  {
    icon: Search, color: '#06b6d4', badge: 'Rayon',
    title: 'Recherche de points',
    description: 'Localisez des points géodésiques nationaux dans un rayon défini (5 à 500 km). Sélectionnez-les sur la carte pour alimenter le calcul des 7 paramètres de Helmert.',
  },
];

const STEPS = [
  { num: '01', title: 'Importez vos données',     description: 'Chargez vos fichiers GSI, DXF ou saisissez des coordonnées manuellement.' },
  { num: '02', title: 'Transformez & calculez',   description: 'Effectuez vos transformations géodésiques et calculs topographiques en quelques clics.' },
  { num: '03', title: 'Visualisez & exportez',    description: 'Affichez les résultats sur carte et exportez dans le format de votre choix.' },
];

const MODULES = [
  { icon: Globe,      color: '#667eea', title: 'Topographie',      desc: 'Carte dark · mesure · points topographiques' },
  { icon: Crosshair,  color: '#10b981', title: 'Transformation',    desc: 'WGS84 ↔ UTM ↔ Laborde · batch CSV'        },
  { icon: Layers,     color: '#f59e0b', title: 'Viewer DXF / CAO', desc: 'Import · dessin · export DXF & PDF'        },
  { icon: FolderOpen, color: '#8b5cf6', title: 'Stockage fichiers', desc: 'Upload · gestion · quota sécurisé'         },
];

export default function HomePage() {
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [visible,   setVisible]   = useState({});
  const isAuth = useAuthStore.isAuthenticated();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      ['features','how','modules','cta'].forEach(id => {
        const el = document.getElementById(id);
        if (!el) return;
        const { top, bottom } = el.getBoundingClientRect();
        if (top < window.innerHeight * 0.85 && bottom > 0)
          setVisible(v => ({ ...v, [id]: true }));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  return (
    <div className="lp">

      {/* ─── NAVBAR ─── */}
      <header className={'lp-nav' + (scrolled ? ' lp-nav--scrolled' : '')}>
        <div className="lp-nav-inner">
          <Link to="/" className="lp-nav-brand">
            <span className="lp-nav-brand-icon"><Zap size={16} /></span>
            <LogoWordmark height={26} />
            <sup className="lp-beta">BETA</sup>
          </Link>
          <nav className="lp-nav-links">
            {[['features','Fonctionnalités'],['how','Comment ça marche'],['modules','Modules']].map(([id,label]) => (
              <button key={id} className="lp-nav-link" onClick={() => scrollTo(id)}>{label}</button>
            ))}
          </nav>
          <div className="lp-nav-actions">
            {isAuth ? (
              <Link to="/expert" className="lp-btn lp-btn--primary">Dashboard <ArrowRight size={14} /></Link>
            ) : (
              <>
                <Link to="/login" className="lp-btn lp-btn--ghost">Connexion</Link>
                <Link to="/login" className="lp-btn lp-btn--primary">Commencer <ArrowRight size={14} /></Link>
              </>
            )}
            <button className="lp-mobile-toggle" onClick={() => setMenuOpen(v => !v)}>
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lp-mobile-menu">
            {[['features','Fonctionnalités'],['how','Comment ça marche'],['modules','Modules']].map(([id,label]) => (
              <button key={id} className="lp-mobile-link" onClick={() => scrollTo(id)}>{label}</button>
            ))}
            <Link to="/login" className="lp-btn lp-btn--primary lp-btn--full">Commencer</Link>
          </div>
        )}
      </header>

      {/* ─── HERO ─── */}
      <section className="lp-hero">
        <div className="lp-hero-bg">
          <div className="lp-orb lp-orb--1" />
          <div className="lp-orb lp-orb--2" />
          <div className="lp-grid" />
        </div>
        <div className="lp-hero-content">
          <div className="lp-badge"><Shield size={13} />Plateforme géospatiale professionnelle </div>
          <h1 className="lp-hero-title">
            Géodésie & Topographie<br />
            <span className="lp-gradient-text">nouvelle génération</span>
          </h1>
          <p className="lp-hero-sub">
            Transformations géodésiques, visualisation DXF et cartographie interactive
            — dans une seule plateforme pensée pour les professionnels.
          </p>
          <div className="lp-hero-cta">
            <Link to="/login" className="lp-btn lp-btn--hero-primary">
              Démarrer gratuitement <ArrowRight size={17} />
            </Link>
            <button className="lp-btn lp-btn--hero-secondary" onClick={() => scrollTo('features')}>
              <Map size={16} /> Voir les fonctionnalités
            </button>
          </div>
          <div className="lp-hero-tags">
            {['WGS84','Laborde Madagascar','UTM 38S/39S','Viewer DXF','7 param.','CartoDB Dark'].map(t => (
              <span key={t} className="lp-tag"><CheckCircle size={11} />{t}</span>
            ))}
          </div>
        </div>
        <div className="lp-hero-preview">
          <div className="lp-preview-card">
            {/* Chrome navigateur */}
            <div className="lp-preview-bar">
              <span className="lp-preview-dot" style={{background:'#ff5f57'}} />
              <span className="lp-preview-dot" style={{background:'#febc2e'}} />
              <span className="lp-preview-dot" style={{background:'#28c840'}} />
              <span className="lp-preview-url">expert/maps · Antananarivo</span>
            </div>

            {/* Carte + UI chrome */}
            <div className="lp-preview-body" style={{position:'relative', overflow:'hidden', background:'#0d1322'}}>

              {/* ── SVG carte Antananarivo — CartoDB Dark Matter ── */}
              <svg viewBox="0 0 390 210" style={{width:'100%',height:'100%',display:'block',position:'absolute',inset:0}}>
                {/* Fond sombre */}
                <rect width="390" height="210" fill="#0d1322"/>

                {/* Blocs urbains (texture de fond) */}
                {[[20,30,55,32],[85,22,40,28],[135,15,60,35],[205,10,50,30],[265,18,55,28],[325,12,55,38],
                  [15,75,48,30],[72,65,55,35],[138,58,65,32],[215,55,55,30],[278,62,58,32],[345,58,40,38],
                  [18,118,52,32],[78,112,60,30],[148,108,58,34],[218,105,52,28],[278,110,55,30],[340,108,45,35],
                  [20,162,55,30],[82,158,58,28],[148,155,55,32],[210,152,60,28],[278,155,52,30],[338,160,45,28]
                ].map(([x,y,w,h],i) => (
                  <rect key={i} x={x} y={y} width={w} height={h} rx="1" fill="#101828" opacity="0.9"/>
                ))}

                {/* Lac Anosy */}
                <ellipse cx="318" cy="178" rx="38" ry="22" fill="#090f1e" stroke="#14253a" strokeWidth="1"/>
                <text x="318" y="182" fill="#1e3050" fontSize="7" textAnchor="middle" fontFamily="sans-serif">Lac Anosy</text>

                {/* ── Routes principales (artères) ── */}
                {/* Araben'ny Fahasoavana — grand axe diagonal NO→SE */}
                <path d="M0,85 Q80,78 155,72 Q230,66 310,60 Q360,57 390,55"
                      stroke="#2a3d5a" strokeWidth="3.5" fill="none"/>
                {/* Lalana ny Fahafahana — axe vertical principal */}
                <path d="M195,0 Q192,50 190,105 Q188,155 186,210"
                      stroke="#253650" strokeWidth="3" fill="none"/>
                {/* Route de Majunga — vers le nord-ouest */}
                <path d="M0,40 Q60,48 115,58 Q160,66 190,72"
                      stroke="#233250" strokeWidth="2.5" fill="none"/>
                {/* RN1 — vers l'ouest */}
                <path d="M0,140 Q70,128 145,115 Q200,106 250,100"
                      stroke="#233250" strokeWidth="2.5" fill="none"/>
                {/* Arabe circonscription — circulaire */}
                <path d="M120,20 Q155,15 190,22 Q225,28 250,48 Q270,68 265,95 Q258,120 240,135"
                      stroke="#1e2e48" strokeWidth="2" fill="none"/>

                {/* ── Routes secondaires ── */}
                <path d="M70,0 Q72,35 74,70 Q76,108 78,145 Q80,175 82,210"   stroke="#182640" strokeWidth="1.5" fill="none"/>
                <path d="M145,0 Q147,40 149,80 Q151,118 153,155 Q155,180 157,210" stroke="#182640" strokeWidth="1.5" fill="none"/>
                <path d="M265,0 Q264,42 263,85 Q262,128 261,168 Q260,185 259,210"  stroke="#182640" strokeWidth="1.5" fill="none"/>
                <path d="M335,0 Q334,45 333,90 Q332,135 331,175 Q330,190 330,210"  stroke="#182640" strokeWidth="1.3" fill="none"/>
                <path d="M0,30  Q130,30 390,28"   stroke="#182640" strokeWidth="1.3" fill="none"/>
                <path d="M0,172 Q130,170 390,168"  stroke="#182640" strokeWidth="1.3" fill="none"/>

                {/* ── Rues mineures (grille Analakely) ── */}
                {[55,100,175,225,295,350].map(x => (
                  <line key={x} x1={x} y1="0" x2={x} y2="210" stroke="#111e30" strokeWidth="0.7"/>
                ))}
                {[47,90,135,180].map(y => (
                  <line key={y} x1="0" y1={y} x2="390" y2={y} stroke="#111e30" strokeWidth="0.7"/>
                ))}

                {/* ── Labels de rues ── */}
                <text x="195" y="68" fill="#2a4060" fontSize="6" textAnchor="middle" fontFamily="sans-serif" transform="rotate(-2,195,68)">Araben'ny Fahasoavana</text>
                <text x="162" y="85" fill="#233255" fontSize="5.5" textAnchor="middle" fontFamily="sans-serif" transform="rotate(88,162,85)">Lalana ny Fahafahana</text>
                <text x="80" y="130" fill="#1e2e48" fontSize="5.5" textAnchor="middle" fontFamily="sans-serif" transform="rotate(-6,80,130)">RN 1</text>

                {/* ── Marqueurs géodésiques (triangles — même style que l'app) ── */}
                {/* Point 1 — sélectionné, violet */}
                <polygon points="155,88 163,102 147,102" fill="#2e1065" stroke="#a78bfa" strokeWidth="1.5" style={{filter:'drop-shadow(0 0 5px rgba(167,139,250,0.7))'}}/>
                <circle cx="155" cy="97" r="2.5" fill="#ede9fe"/>

                {/* Point 2 — normal, cyan */}
                <polygon points="232,94 238,104 226,104" fill="#082f49" stroke="#22d3ee" strokeWidth="1.5" style={{filter:'drop-shadow(0 0 4px rgba(6,182,212,0.6))'}}/>
                <circle cx="232" cy="100" r="2" fill="#67e8f9"/>

                {/* Point 3 — normal, cyan */}
                <polygon points="108,76 114,86 102,86" fill="#082f49" stroke="#22d3ee" strokeWidth="1.5"/>
                <circle cx="108" cy="82" r="2" fill="#67e8f9"/>

                {/* Point 4 — survol, ambre */}
                <polygon points="290,82 298,96 282,96" fill="#431407" stroke="#fb923c" strokeWidth="1.8" style={{filter:'drop-shadow(0 0 6px rgba(251,146,60,0.7))'}}/>
                <circle cx="290" cy="90" r="3" fill="#fed7aa"/>

                {/* ── Cercle de rayon de recherche ── */}
                <circle cx="195" cy="95" r="55" fill="none" stroke="rgba(129,140,248,0.18)" strokeWidth="1" strokeDasharray="4 6"/>

                {/* ── Contrôles Leaflet (scale) ── */}
                <rect x="8" y="192" width="48" height="8" rx="1" fill="#0d1322" stroke="#253650" strokeWidth="0.8"/>
                <text x="32" y="198" fill="#2a4060" fontSize="5" textAnchor="middle" fontFamily="monospace">500 m</text>
              </svg>

              {/* ── Toolbar droite (mockup) ── */}
              <div style={{
                position:'absolute', right:8, top:8,
                display:'flex', flexDirection:'column', gap:3,
                background:'rgba(7,13,30,0.88)', border:'1px solid rgba(129,140,248,0.12)',
                borderRadius:10, padding:5,
              }}>
                {['⊕','↖','✏','☰','XY','⊡'].map((ic,i) => (
                  <div key={i} style={{
                    width:22, height:22, borderRadius:5, display:'flex',
                    alignItems:'center', justifyContent:'center',
                    fontSize:10, color: i===1 ? '#818cf8' : '#475569',
                    background: i===1 ? 'rgba(129,140,248,0.14)' : 'transparent',
                    border: i===1 ? '1px solid rgba(129,140,248,0.35)' : '1px solid transparent',
                  }}>{ic}</div>
                ))}
              </div>

              {/* ── Barre de statut ── */}
              <div style={{
                position:'absolute', bottom:0, left:0, right:0,
                background:'rgba(5,10,24,0.94)', borderTop:'1px solid rgba(129,140,248,0.07)',
                padding:'3px 8px', display:'flex', alignItems:'center', justifyContent:'space-between',
                fontSize:7, fontFamily:'monospace',
              }}>
                <span style={{color:'#818cf8', fontWeight:700}}>LABORDE</span>
                <span style={{color:'#64748b'}}>X : 498 234.12 &nbsp; Y : 833 092.45 m</span>
                <span style={{color:'#10b981', fontWeight:700}}>WS ●</span>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section id="features" className={'lp-section lp-features' + (visible.features ? ' lp-visible' : '')}>
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-label">Fonctionnalités</span>
            <h2 className="lp-section-title">Tout ce dont vous avez besoin</h2>
            <p className="lp-section-sub">Un écosystème complet pour vos projets de géodésie et topographie.</p>
          </div>
          <div className="lp-features-grid">
            {FEATURES.map(({ icon: Icon, color, title, description, badge }, i) => (
              <div key={title} className="lp-feature-card" style={{ animationDelay: i * 80 + 'ms' }}>
                <div className="lp-feature-icon" style={{ background: color + '18', color }}>
                  <Icon size={22} />
                </div>
                <span className="lp-feature-badge">{badge}</span>
                <h3 className="lp-feature-title">{title}</h3>
                <p className="lp-feature-desc">{description}</p>
                <div className="lp-feature-arrow"><ChevronRight size={16} /></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how" className={'lp-section lp-how' + (visible.how ? ' lp-visible' : '')}>
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-label">Workflow</span>
            <h2 className="lp-section-title">Comment ça marche</h2>
            <p className="lp-section-sub">3 étapes pour passer de vos données brutes à vos résultats.</p>
          </div>
          <div className="lp-steps">
            {STEPS.map(({ num, title, description }, i) => (
              <div key={num} className="lp-step" style={{ animationDelay: i * 120 + 'ms' }}>
                <div className="lp-step-num">{num}</div>
                <div className="lp-step-body">
                  <h3 className="lp-step-title">{title}</h3>
                  <p className="lp-step-desc">{description}</p>
                </div>
                {i < STEPS.length - 1 && <div className="lp-step-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── DXF VIEWER SHOWCASE ─── */}
      <section id="dxf" className={'lp-section lp-dxf-section' + (visible.modules ? ' lp-visible' : '')}>
        <div className="lp-container">
          <div className="lp-dxf-grid">

            {/* ── Texte gauche ── */}
            <div className="lp-dxf-text">
              <span className="lp-badge" style={{marginBottom:'1rem', display:'inline-flex'}}>
                <Layers size={13} /> Viewer DXF / CAO
              </span>
              <h2 className="lp-dxf-title">
                Visualisez et dessinez<br />
                <span className="lp-gradient-text">comme dans AutoCAD</span>
              </h2>
              <p className="lp-dxf-desc">
                Visualisez, dessinez et exportez vos plans DXF directement dans le navigateur — sans logiciel supplémentaire.
              </p>

              <ul className="lp-dxf-list">
                {[
                  { icon: '↗', label: 'Import',  detail: 'Glissez votre fichier .dxf' },
                  { icon: '✏', label: 'Dessin',  detail: 'Ligne, Cercle, Rectangle, Hachure' },
                  { icon: '↙', label: 'Export',  detail: 'DXF natif ou PDF à l\'échelle exacte' },
                ].map(({ icon, label, detail }) => (
                  <li key={label} className="lp-dxf-item">
                    <span className="lp-dxf-item-icon">{icon}</span>
                    <div>
                      <span className="lp-dxf-item-label">{label}</span>
                      <span className="lp-dxf-item-detail"> — {detail}</span>
                    </div>
                  </li>
                ))}
              </ul>

              <Link
                to="/login"
                state={{ from: { pathname: '/cad' } }}
                className="lp-btn lp-btn--hero-primary"
                style={{marginTop:'2rem', display:'inline-flex'}}
              >
                Ouvrir le Viewer DXF <ArrowRight size={16} />
              </Link>
            </div>

            {/* ── Mockup viewer droite ── */}
            <div className="lp-dxf-mockup">
              <div className="lp-dxf-window">

                {/* Barre de titre */}
                <div className="lp-dxf-titlebar">
                  <span style={{color:'#4a9eff', fontWeight:700, fontSize:11}}>MadJeo CAD</span>
                  <span style={{color:'#444', margin:'0 6px'}}>|</span>
                  <span style={{color:'#666', fontSize:10}}>plan_topographique.dxf</span>
                </div>

                {/* Barre de menus */}
                <div className="lp-dxf-menubar">
                  {['Fichier','Calques','Naviguer','Config','Exporter PDF'].map(m => (
                    <span key={m} className="lp-dxf-menuitem">{m}</span>
                  ))}
                </div>

                {/* Zone de dessin SVG */}
                <div style={{position:'relative', flex:1, background:'#1e1e1e', overflow:'hidden'}}>
                  <svg viewBox="0 0 340 190" style={{width:'100%', height:'100%', display:'block'}}>
                    {/* Fond */}
                    <rect width="340" height="190" fill="#1e1e1e"/>

                    {/* Grille légère */}
                    {[40,80,120,160,200,240,280,320].map(x => (
                      <line key={x} x1={x} y1="0" x2={x} y2="190" stroke="#252525" strokeWidth="0.5"/>
                    ))}
                    {[30,60,90,120,150,180].map(y => (
                      <line key={y} x1="0" y1={y} x2="340" y2={y} stroke="#252525" strokeWidth="0.5"/>
                    ))}

                    {/* Calque MURS (blanc) — plan de bâtiment */}
                    <rect x="60" y="25" width="220" height="130" fill="none" stroke="#cccccc" strokeWidth="2"/>
                    {/* Mur intérieur vertical */}
                    <line x1="160" y1="25" x2="160" y2="155" stroke="#cccccc" strokeWidth="2"/>
                    {/* Mur intérieur horizontal */}
                    <line x1="60" y1="90" x2="160" y2="90" stroke="#cccccc" strokeWidth="2"/>

                    {/* Calque PORTES (cyan) */}
                    <path d="M 95,155 A 30,30 0 0 1 125,155" fill="none" stroke="#00aaff" strokeWidth="1.2"/>
                    <line x1="95" y1="155" x2="95" y2="148" stroke="#00aaff" strokeWidth="1.2"/>
                    <path d="M 185,25 A 25,25 0 0 0 210,25" fill="none" stroke="#00aaff" strokeWidth="1.2"/>
                    <line x1="185" y1="25" x2="185" y2="32" stroke="#00aaff" strokeWidth="1.2"/>

                    {/* Calque COLONNES (jaune) — petits cercles */}
                    {[[60,25],[160,25],[280,25],[60,90],[60,155],[160,155],[280,155]].map(([cx,cy],i) => (
                      <circle key={i} cx={cx} cy={cy} r="5" fill="#1e1e1e" stroke="#ffcc00" strokeWidth="1.5"/>
                    ))}

                    {/* Calque CÔTES (vert) */}
                    <line x1="60" y1="170" x2="280" y2="170" stroke="#00cc66" strokeWidth="0.8"/>
                    <line x1="60" y1="165" x2="60" y2="175" stroke="#00cc66" strokeWidth="0.8"/>
                    <line x1="280" y1="165" x2="280" y2="175" stroke="#00cc66" strokeWidth="0.8"/>
                    <text x="170" y="178" fill="#00cc66" fontSize="7" textAnchor="middle" fontFamily="monospace">22.00 m</text>

                    <line x1="18" y1="25" x2="18" y2="155" stroke="#00cc66" strokeWidth="0.8"/>
                    <line x1="13" y1="25" x2="23" y2="25" stroke="#00cc66" strokeWidth="0.8"/>
                    <line x1="13" y1="155" x2="23" y2="155" stroke="#00cc66" strokeWidth="0.8"/>
                    <text x="10" y="93" fill="#00cc66" fontSize="7" textAnchor="middle" fontFamily="monospace" transform="rotate(-90,10,93)">13.00 m</text>

                    {/* Cartouche titre (bottom) */}
                    <rect x="60" y="158" width="220" height="0" fill="none"/>

                    {/* Point de snap actif */}
                    <circle cx="160" cy="90" r="4" fill="none" stroke="#ffff00" strokeWidth="1" strokeDasharray="2 2"/>
                  </svg>

                  {/* Toolbar gauche */}
                  <div style={{
                    position:'absolute', left:4, top:6,
                    display:'flex', flexDirection:'column', gap:3,
                    background:'rgba(25,30,45,0.9)', border:'1px solid #333',
                    borderRadius:5, padding:4,
                  }}>
                    {[{ic:'↖',a:true},{ic:'✏',a:false},{ic:'+',a:false},{ic:'−',a:false},{ic:'☰',a:false}].map(({ic,a},i) => (
                      <div key={i} style={{
                        width:18, height:18, borderRadius:3, display:'flex',
                        alignItems:'center', justifyContent:'center', fontSize:9,
                        color: a ? '#fff' : '#666',
                        background: a ? 'rgba(0,80,180,0.85)' : 'transparent',
                        border: a ? '1px solid #4a9eff' : '1px solid transparent',
                      }}>{ic}</div>
                    ))}
                  </div>
                </div>

                {/* Barre de statut */}
                <div className="lp-dxf-statusbar">
                  <span style={{color:'#fff', flex:1}}>SÉLECTION – Cliquer pour sélectionner</span>
                  <span style={{color:'#c8e8ff', fontFamily:'monospace', fontSize:9}}>
                    X: 160.000 &nbsp; Y: 90.000 &nbsp;|&nbsp; Calque: MURS
                  </span>
                </div>
              </div>

              {/* Badge "Disponible maintenant" */}
              <div className="lp-dxf-badge-now">
                <CheckCircle size={12} /> Disponible maintenant
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─── MODULES ─── */}
      <section id="modules" className={'lp-section lp-modules-section' + (visible.modules ? ' lp-visible' : '')}>
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-label">Modules</span>
            <h2 className="lp-section-title">Une plateforme, plusieurs outils</h2>
          </div>
          <div className="lp-modules-grid">
            {MODULES.map(({ icon: Icon, color, title, desc }, i) => (
              <div key={title} className="lp-module-card" style={{ animationDelay: i * 80 + 'ms' }}>
                <div className="lp-module-icon" style={{ background: color + '18', color }}>
                  <Icon size={28} />
                </div>
                <h3 className="lp-module-title">{title}</h3>
                <p className="lp-module-desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section id="cta" className={'lp-cta' + (visible.cta ? ' lp-visible' : '')}>
        <div className="lp-cta-orb lp-cta-orb--1" />
        <div className="lp-cta-orb lp-cta-orb--2" />
        <div className="lp-container lp-cta-inner">
          <div className="lp-cta-stars">
            {[0,1,2,3,4].map(i => <Star key={i} size={16} fill="currentColor" />)}
          </div>
          <h2 className="lp-cta-title">Prêt à démarrer votre prochain<br />projet géodésique ?</h2>
          <p className="lp-cta-sub">Créez un compte et accédez immédiatement à tous les outils.</p>
          <div className="lp-hero-cta">
            <Link
              to="/login"
              state={{ from: { pathname: '/expert' } }}
              className="lp-btn lp-btn--hero-primary"
            >
              Dashboard — Topographie <ArrowRight size={17} />
            </Link>
            <Link
              to="/login"
              state={{ from: { pathname: '/cad' } }}
              className="lp-btn lp-btn--hero-secondary"
            >
              <Layers size={16} /> Viewer DXF / CAO
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-top">
            <div className="lp-footer-brand">
              <div className="lp-nav-brand" style={{ marginBottom: '0.75rem' }}>
                <span className="lp-nav-brand-icon"><Zap size={16} /></span>
                <LogoWordmark height={26} madColor="#ffffff" />
                <sup className="lp-beta">BETA</sup>
              </div>
              <p className="lp-footer-tagline">La plateforme géospatiale professionnelle pour Madagascar.</p>
              <div className="lp-footer-social">
                <a href="mailto:contact@madjeo.mg" className="lp-social-link"><Mail size={17} /></a>
                <a href="#" className="lp-social-link"><Linkedin size={17} /></a>
                <a href="#" className="lp-social-link"><Github size={17} /></a>
              </div>
            </div>
            <div className="lp-footer-links">
              <div className="lp-footer-col">
                <h4>Produit</h4>
                <button onClick={() => scrollTo('features')}>Fonctionnalités</button>
                <button onClick={() => scrollTo('modules')}>Modules</button>
                <button onClick={() => scrollTo('how')}>Comment ça marche</button>
              </div>
              <div className="lp-footer-col">
                <h4>Compte</h4>
                <Link to="/login">Connexion</Link>
                <Link to="/login">S'inscrire</Link>
              </div>
              <div className="lp-footer-col">
                <h4>Contact</h4>
                <span><Phone size={13} /> +33 7 44 20 00 18</span>
                <span><Mail size={13} /> contact@madjeo.mg</span>
              </div>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span>© {new Date().getFullYear()} Madjeo. Tous droits réservés.</span>
            <span>Fait avec ♥ pour la géodésie malgache</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
